import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  SUPPRESSION_DURATIONS,
  SUPPRESSION_SCOPE_LABELS,
  SuppressionInput,
  SuppressionScope,
  defaultSuppressionScope,
  suppressionScopeChoices,
} from '~/services/AriAnomaliesService';

import { AnomalyCase } from './types';

const DEFAULT_DAYS = 7;

interface Props {
  anomalyCase: AnomalyCase | null;
  onClose: () => void;
  onConfirm: (input: SuppressionInput) => void;
}

/**
 * Suppression is not a dismissal: dismissing judges one case, suppressing silences a shape of
 * alert for a while, which is why scope and an expiry are asked for and a reason is mandatory.
 *
 * The scope is shown rather than inferred silently. The default is the narrow one, but the
 * widest option silences a rule at every property and nobody should reach it without reading it.
 */
export default function SuppressCaseDialog({ anomalyCase: c, onClose, onConfirm }: Props) {
  const hasProperty = !!c?.propertyId;
  const scopes = useMemo(() => suppressionScopeChoices(hasProperty), [hasProperty]);

  const [scope, setScope] = useState<SuppressionScope>(defaultSuppressionScope(hasProperty));
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (c) {
      setScope(defaultSuppressionScope(!!c.propertyId));
      setDays(DEFAULT_DAYS);
      setReason('');
    }
  }, [c]);

  const confirm = () => {
    if (!c) {
      return;
    }
    onConfirm({ scope, ruleKey: c.ruleKey, propertyId: c.propertyId || null, reason: reason.trim(), days });
  };

  return (
    <Dialog open={!!c} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Suppress {c?.caseId}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {c?.ruleLabel} · {c?.propertyName}
        </Typography>

        <FormControl fullWidth size="small" required sx={{ mt: 2 }}>
          <InputLabel id="suppress-scope">What to silence</InputLabel>
          <Select
            labelId="suppress-scope"
            label="What to silence"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            {scopes.map((s) => (
              <MenuItem key={s} value={s}>
                {SUPPRESSION_SCOPE_LABELS[s]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" required sx={{ mt: 2 }}>
          <InputLabel id="suppress-duration">For how long</InputLabel>
          <Select
            labelId="suppress-duration"
            label="For how long"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {SUPPRESSION_DURATIONS.map((d) => (
              <MenuItem key={d.days} value={d.days}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label="Why"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          // Stays visible once typing starts: it is the sentence that explains why a reason is
          // mandatory, and hiding it the moment someone begins is when it is most needed.
          helperText="A suppression needs a reason — it silences future alerts, not just this one."
          error={!reason.trim()}
          sx={{ mt: 2 }}
        />

        {!hasProperty && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This case has no property, so it can only be silenced rule-wide — every property.
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          Cases this covers that are still pending or acknowledged are silenced straight away. Ones already resolved or
          dismissed keep the status they were given.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={!reason.trim()} onClick={confirm}>
          Suppress
        </Button>
      </DialogActions>
    </Dialog>
  );
}
